import React, { useState } from "react";
import {
    Button,
    Col,
    DatePicker,
    Row,
    Select,
    Spin,
    Card,
    Form,
    Checkbox
} from "antd";
import "./GeneralLedger.css";
import { getAllBookEntryList, getAllReversedData } from "./GeneralLedgerService";
import { getCurrentDateTimeString } from "../../Utils/Util";
import BranchLocationSelector from "../BranchLocationSelector";
import { exportBookEntryList } from "../../Utils/GeneralLedger/ExportBookEntryList";

const { Option } = Select;
const { RangePicker } = DatePicker;

const BookEntryList = ({
    loginPlaceId,
    districtData = [],
    districtName,
    divisionalId,
    branchId,
    branchName
}) => {
    const [formState, setFormState] = useState({
        dates: [],
        authorized: "0",
        transactionType: "all",
        isReversed: false
    });

    const [loading, setLoading] = useState(false);
    const [locationData, setLocationData] = useState({});

    // ✅ Generic form state updater
    const updateFormState = (key, value) => {
        setFormState((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    // ✅ Handle transaction type change
    const handleHeaderChange = (value) => {
        updateFormState("transactionType", value);
    };

    // ✅ Handle auth type change
    const handleLedgerAccChangeReport = (value) => {
        updateFormState("authorized", value);
    };

    // ✅ Disable button logic
    const isGenerateDisabled =
        !formState.dates || formState.dates.length !== 2;

    const handleCheckboxChange = (e) => {
        const checked = e.target.checked;

        setFormState((prev) => ({
            ...prev,
            isReversed: checked,
            transactionType: checked ? "all" : prev.transactionType,
            authorized: checked ? "0" : prev.authorized
        }));
    };



    // ✅ API Call
    const handleBookEntryListApi = () => {
        const [startDate, endDate] = formState.dates || [];

        const data = {
            branchId:
                loginPlaceId === "1"
                    ? branchId
                    : locationData.branchId,
            authorizedType: formState.authorized,
            startDate: startDate,
            endDate: endDate,
            typeCode:
                formState.transactionType === "all"
                    ? null
                    : formState.transactionType,
        };

        setLoading(true);

        const branch = loginPlaceId === "1" ? branchName : locationData.branchName;


        if (!formState.isReversed) {
            const columns = [
                { header: "Date", key: "valueDate", type: "date", width: 20 },
                { header: "Transaction Type", key: "transactionType", align: "center", width: 25 },
                { header: "Transaction No", key: "transactionNumber", align: "center", width: 25 },
                { header: "Account", key: "account", align: "center", width: 20 },
                { header: "Memo", key: "memo", wrap: true, width: 50 },
                { header: "User", key: "user", width: 40 }
            ];

            getAllBookEntryList(data)
                .then((res) => {
                    exportBookEntryList({
                        data: res.data,
                        columns: columns,
                        fileName:
                            branch + "-" + getCurrentDateTimeString(),
                        selectedDate: formState.dates,
                        title: "Book entry Report - " + branch,
                        loginPlaceId: loginPlaceId
                    });
                })
                .catch((err) => console.error(err))
                .finally(() => setLoading(false));
        } else {
            const columns = [
                { header: "Date", key: "valueDate", type: "date", width: 20 },
                { header: "Transaction Type", key: "transactionType", align: "center", width: 25 },
                { header: "Transaction No", key: "transactionNumber", align: "center", width: 25 },
                { header: "Account", key: "account", align: "center", width: 20 },
                { header: "Amount", key: "amount", type: "currency", align: "center", width: 20 },
                { header: "Memo", key: "memo", wrap: true, width: 50 },
                { header: "User", key: "user", width: 40 }
            ];
            getAllReversedData(data)
                .then((res) => {
                    exportBookEntryList({
                        data: res.data,
                        columns: columns,
                        fileName:
                            branch + "-" + getCurrentDateTimeString(),
                        selectedDate: formState.dates,
                        title: "Reverse Transaction Report - " + branch,
                        loginPlaceId: loginPlaceId
                    });
                })
                .catch((err) => console.error(err))
                .finally(() => setLoading(false));
        }
    };

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    <Row gutter={[16, 16]} justify="space-around">

                        {/* Branch Selector */}
                        <BranchLocationSelector
                            loginPlaceId={loginPlaceId}
                            districtData={districtData}
                            districtName={districtName}
                            divisionalId={divisionalId}
                            onChange={setLocationData}
                        />

                        {/* Transaction Type */}
                        <Col span={24}>
                            <Form.Item label="Type">
                                <Select
                                    value={formState.transactionType}
                                    onChange={handleHeaderChange}
                                    style={{ width: "100%" }}
                                    disabled={formState.isReversed}

                                >
                                    <Option value="all"><b>ALL</b></Option>
                                    <Option value="JN"><b>JN</b></Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Auth Type */}
                        <Col span={24}>
                            <Form.Item label="Auth type">
                                <Select
                                    value={formState.authorized}
                                    onChange={handleLedgerAccChangeReport}
                                    style={{ width: "100%" }}
                                    disabled={formState.isReversed}
                                >
                                    <Option value="0"><b>Approved</b></Option>
                                    <Option value="1"><b>Unauthorized</b></Option>
                                    <Option value="-1"><b>Rejected</b></Option>
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Date Range */}
                        <Col span={24}>
                            <Form.Item label="Date">
                                <RangePicker
                                    onChange={(dates) =>
                                        updateFormState("dates", dates)
                                    }
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={24} style={{ textAlign: 'right' }}>
                            <Form.Item>
                                <Form.Item name={"reversedTransactionList"} valuePropName={"checked"} noStyle>
                                    <Checkbox onChange={handleCheckboxChange}>Reversed Transaction List</Checkbox>
                                </Form.Item>
                            </Form.Item>
                        </Col>

                        {/* Generate Button */}
                        <Col span={24} style={{ textAlign: "right" }}>
                            <Form.Item>
                                <Button
                                    onClick={handleBookEntryListApi}
                                    disabled={loading || isGenerateDisabled}
                                    className="generate-btn"
                                    type="primary"
                                >
                                    {loading ? <Spin /> : "Generate Report"}
                                </Button>
                            </Form.Item>
                        </Col>

                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default BookEntryList;